import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import HpQuiz from './HpQuiz';
import { usePet } from '../contexts/PetContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HpStatsExtra({ refreshKey }) {
  const [stats, setStats] = useState(null);
  const [extra, setExtra] = useState(null);
  const [busy, setBusy] = useState('');
  const { refreshPet } = usePet();

  const load = () => axios.get(`${API}/hp-daily/stats`, { withCredentials: true }).then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  useEffect(() => {
    const onStart = (e) => {
      document.querySelector('[data-testid="hp-stats"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      start(e.detail?.part_id);
    };
    window.addEventListener('hp-extra-start', onStart);
    return () => window.removeEventListener('hp-extra-start', onStart);
  });

  const start = async (partId) => {
    if (busy) return;
    setBusy('gen');
    setExtra(null);
    try {
      const body = typeof partId === 'string' ? { part_id: partId } : {};
      const { data } = await axios.post(`${API}/hp-daily/extra/generate`, body, { withCredentials: true, timeout: 90000 });
      setExtra(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Hampus tappade frågorna 🐹'); }
    setBusy('');
  };
  const submit = async (answers) => {
    setBusy('submit');
    try {
      const { data } = await axios.post(`${API}/hp-daily/extra/${extra.id}/submit`, { answers }, { withCredentials: true });
      setExtra(data);
      toast.success(`🐹 Extra-pass: ${data.score}/${data.questions.length} rätt · +${data.xp} XP`);
      refreshPet?.();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte lämna in'); }
    setBusy('');
  };

  if (!stats) return null;
  const weak = stats.parts.find((p) => p.id === stats.weakest);

  return (
    <section className="neo-card p-4 bg-white space-y-3 scroll-mt-24" data-testid="hp-stats">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="font-['Fredoka'] font-bold text-lg">📊 Dina delar</h4>
        <span className="text-[10px] opacity-60">Dag 9–14 tränar automatiskt dina svagaste delar</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {stats.parts.map((p) => (
          <div key={p.id} className={`rounded-xl p-2 border-2 ${p.id === stats.weakest ? 'border-[#C24672]' : 'border-black/10'}`} data-testid={`hp-stat-${p.id}`}>
            <div className="text-xs font-black">{p.emoji} {p.name}</div>
            <div className="h-1.5 rounded-full bg-black/10 mt-1.5 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${p.accuracy ?? 0}%`, background: p.color }} />
            </div>
            <div className="text-[10px] opacity-60 mt-1">{p.accuracy === null ? 'ej testad' : `${p.accuracy}% · ${p.correct}/${p.total}`}</div>
          </div>
        ))}
      </div>
      {!extra && (
        <button onClick={start} disabled={!!busy} className="neo-btn px-4 py-2 bg-[#FFC8DD] font-bold text-sm flex items-center gap-1" data-testid="hp-extra-start">
          {busy === 'gen' ? <><Loader2 className="w-4 h-4 animate-spin" /> Hampus hämtar frågor…</> : `💪 Extra-pass i ${weak ? `${weak.emoji} ${weak.name} (din svagaste hittills)` : 'en slumpad del'}`}
        </button>
      )}
      {extra && (
        <div className="space-y-2" data-testid="hp-extra-pass">
          <div className="text-sm font-black">Extra-pass · {extra.part.emoji} {extra.part.name}</div>
          <HpQuiz quiz={extra} onSubmit={submit} busy={busy === 'submit'} prefix="hp-extra" doneText="Varje extra-pass gör din svaga del lite starkare 💪" />
          {extra.submitted && (
            <button onClick={() => setExtra(null)} className="text-xs underline opacity-70" data-testid="hp-extra-close">Klar, stäng extra-passet</button>
          )}
        </div>
      )}
    </section>
  );
}
